<div class="stk-page">
  <app-headerhome></app-headerhome>

  <main class="stk-shell">
    <button type="button" class="stk-back-button" (click)="backToPayment('passenger')">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Back to passenger details
    </button>

    <ol class="stk-progress" aria-label="Booking progress">
      <li class="is-complete">
        <span class="progress-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
        </span>
        <strong>Seats</strong>
        <i></i>
      </li>
      <li class="is-complete">
        <span class="progress-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
        </span>
        <strong>Passenger details</strong>
        <i></i>
      </li>
      <li class="is-current">
        <span class="progress-circle">3</span>
        <strong>Payment</strong>
      </li>
    </ol>

    <div class="stk-content-grid">
      <section class="stk-waiting-card" [class.is-failed]="paymentState === 'failed'" aria-live="polite">
        @if (paymentState === 'failed') {
          <div class="stk-failed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
              <circle cx="12" cy="12" r="8.5"/>
              <path d="M12 7.5v5.5M12 16.5h.01"/>
            </svg>
          </div>
          <h1>Payment didn’t go through</h1>
          <p role="alert">{{ errorMessage || 'The payment did not go through. Your booking and passenger details are saved.' }}</p>

          <div class="stk-failed-actions">
            <button type="button" class="stk-retry-button" (click)="backToPayment('payment')">Retry payment</button>
            <button type="button" class="stk-secondary-button" (click)="backToPayment('payment')">Choose another method</button>
            <button type="button" class="stk-secondary-button" (click)="contactSupport()">Contact support</button>
          </div>
        } @else {
          <div class="stk-phone-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" aria-hidden="true">
              <rect x="7.5" y="2.5" width="9" height="19" rx="1.8"/>
              <path d="M10.5 18.5h3"/>
            </svg>
          </div>

          <div class="stk-dots" aria-hidden="true">
            <span></span><span></span><span></span>
          </div>

          <h1>Check your phone</h1>
          <p>
            We sent a payment request for
            <strong>{{ payableCurrencyLabel }} {{ payableAmount | number }}</strong>
            @if (paymentPhone) { to <strong>{{ paymentPhone }}</strong> }.
          </p>
          <p>Approve or cancel the request on your phone. This page will update automatically.</p>

          @if (isError && errorMessage) {
            <div class="stk-status-message" role="alert">{{ errorMessage }}</div>
          }
          @if (statusMessage) {
            <div class="stk-status-message is-success" role="status">{{ statusMessage }}</div>
          }

          <div class="stk-actions">
            <div>
              <button type="button" class="stk-secondary-button" (click)="backToPayment('payment')">Use another number</button>
              <button type="button" class="stk-secondary-button" (click)="backToPayment('payment')">Change method</button>
            </div>
            <button type="button" class="stk-resend-button" [disabled]="isResending || resendSeconds > 0" (click)="resendPrompt()">
              @if (isResending) {
                Resending prompt…
              } @else if (resendSeconds > 0) {
                Resend prompt in {{ resendSeconds }}s
              } @else {
                Resend prompt
              }
            </button>
          </div>
        }
      </section>

      <aside class="stk-summary-card">
        <div class="summary-card-heading">
          <span class="summary-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-4 2-4-2-3 2V5a2 2 0 0 1 2-2Z"/><path d="M9 8h6M9 12h6"/></svg>
          </span>
          <div><h2>Booking summary</h2><p>Your payment is being processed</p></div>
        </div>
        @for (journey of journeySummaries; track journey.label) {
          <section class="stk-journey-summary">
            <div class="summary-journey-heading">
              <small>{{ journey.label }}</small>
              <span>{{ journey.date }}</span>
            </div>
            <h3>{{ journey.from }} → {{ journey.to }}</h3>
            <p class="journey-copy">
              {{ formatJourneyTime(journey.details?.result?.departure_time) }} → {{ formatJourneyTime(journey.details?.result?.arrival_time) }}
            </p>
            <dl class="summary-detail-grid">
              <div><dt>Boarding</dt><dd>{{ journey.details?.pickup_location }}</dd></div>
              <div><dt>Drop-off</dt><dd>{{ journey.details?.dropoff_location }}</dd></div>
              <div><dt>Seats</dt><dd>@for (seat of journey.seats; track seat.id; let last = $last) { {{ seat.id }}{{ last ? '' : ', ' }} }</dd></div>
            </dl>
            <dl>
              @for (group of journey.fareGroups; track group.label) {
                <div><dt>{{ group.label }} × {{ group.count }}</dt><dd>{{ payableCurrencyLabel }} {{ group.total | number }}</dd></div>
              }
              @if (journey.bookingFee) {
                <div><dt>Booking fee</dt><dd>{{ payableCurrencyLabel }} {{ journey.bookingFee | number }}</dd></div>
              }
              <div class="stk-subtotal"><dt>Subtotal</dt><dd>{{ payableCurrencyLabel }} {{ journey.total | number }}</dd></div>
            </dl>
          </section>
        }
        @if (bookingSummaryPassengers.length) {
          <section class="stk-passenger-summary">
            <div class="summary-section-title">
              <span aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0"/></svg></span>
              <strong>Passenger details</strong>
            </div>
            @for (passenger of bookingSummaryPassengers; track passenger.seatNumber; let passengerIndex = $index) {
              <h4><span class="summary-passenger-badge">{{ passengerIndex + 1 }}</span>Passenger {{ passengerIndex + 1 }}</h4>
              <dl>
                <div><dt>Full name</dt><dd>{{ passenger.fullName }}</dd></div>
                <div><dt>Phone number</dt><dd>{{ passenger.phoneNumber }}</dd></div>
                <div><dt>Seat number</dt><dd>{{ passenger.seatNumber }}</dd></div>
                <div><dt>Amount</dt><dd>{{ payableCurrencyLabel }} {{ passenger.amount | number }}</dd></div>
              </dl>
            }
          </section>
        }
        <div class="stk-total"><strong>Total payable</strong><b>{{ payableCurrencyLabel }} {{ payableAmount | number }}</b></div>
      </aside>
    </div>
  </main>
</div>
